import { useState, useRef } from 'react';
import type { Card, DeckEntry } from '@mtg-deck-tool/shared';
import { parseDeckList } from '../../lib/deck-parser.js';
import { api, ApiClientError } from '../../lib/api-client.js';
import { useDeckStore } from '../../stores/deck-store.js';

interface DeckImportModalProps {
  onClose: () => void;
}

type Step = 'input' | 'loading' | 'confirm';

function parseMoxfieldDeckId(url: string): string | null {
  const match = /moxfield\.com\/decks\/([A-Za-z0-9_-]+)/i.exec(url);
  return match?.[1] ?? null;
}

export function DeckImportModal({ onClose }: DeckImportModalProps) {
  const [text, setText] = useState('');
  const [step, setStep] = useState<Step>('input');
  const [notFound, setNotFound] = useState<string[]>([]);
  const [pendingEntries, setPendingEntries] = useState<DeckEntry[]>([]);
  const [pendingSideboard, setPendingSideboard] = useState<Card[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [moxfieldUrl, setMoxfieldUrl] = useState('');
  const [moxfieldUrlError, setMoxfieldUrlError] = useState<string | null>(null);
  const [pendingMoxfieldDeckId, setPendingMoxfieldDeckId] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('Resolving cards…');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const importEntries = useDeckStore((s) => s.importEntries);
  const addToSideboard = useDeckStore((s) => s.addToSideboard);
  const setMoxfieldDeckId = useDeckStore((s) => s.setMoxfieldDeckId);
  const setImportLoading = useDeckStore((s) => s.setImportLoading);

  const handleImport = async () => {
    const parsed = parseDeckList(text);
    if (parsed.length === 0) {
      setError('No cards found in the pasted text.');
      return;
    }

    setError(null);
    setLoadingMessage('Resolving cards…');
    setStep('loading');

    const names = [...new Set(parsed.map((p) => p.name))];
    try {
      const { found, notFound: missing } = await api.resolveCards(names);

      // Map resolved cards back to parsed entries (with qty + commander flag)
      // Index by full name AND front-face name (for DFCs like "Sink into Stupor // Soporific Springs")
      const resolvedMap = new Map<string, typeof found[number]>();
      for (const c of found) {
        resolvedMap.set(c.name.toLowerCase(), c);
        if (c.name.includes(' // ')) {
          resolvedMap.set(c.name.split(' // ')[0]!.toLowerCase(), c);
        }
      }
      const entries: DeckEntry[] = [];
      const seenIds = new Set<string>();

      for (const p of parsed) {
        const card = resolvedMap.get(p.name.toLowerCase());
        if (!card) continue;
        if (seenIds.has(card.id)) {
          // Accumulate quantity for duplicate name rows
          const existing = entries.find((e) => e.card.id === card.id);
          if (existing) { existing.quantity += p.quantity; existing.isCommander ||= p.isCommander; }
          continue;
        }
        seenIds.add(card.id);
        entries.push({ card, quantity: p.quantity, isCommander: p.isCommander });
      }

      setPendingEntries(entries);
      setPendingSideboard([]);
      setNotFound(missing);
      setPendingMoxfieldDeckId(null);
      setStep('confirm');
    } catch (err) {
      setStep('input');
      setError(err instanceof ApiClientError ? err.message : 'Failed to resolve cards. Is the server running?');
    }
  };

  const handleImportUrl = async () => {
    const deckId = parseMoxfieldDeckId(moxfieldUrl.trim());
    if (!deckId) {
      setMoxfieldUrlError('Enter a valid Moxfield deck URL (e.g. https://www.moxfield.com/decks/…)');
      return;
    }
    setMoxfieldUrlError(null);
    setError(null);
    setLoadingMessage('Fetching deck from Moxfield…');
    setStep('loading');

    try {
      const { deck, sideboard, notFound: missing } = await api.resolveMoxfieldDeck(deckId);
      setPendingEntries(deck);
      setPendingSideboard(sideboard);
      setNotFound(missing);
      setPendingMoxfieldDeckId(deckId);
      setStep('confirm');
    } catch (err) {
      setStep('input');
      if (err instanceof ApiClientError) {
        if (err.status === 404) {
          setMoxfieldUrlError('Deck not found. Make sure the deck is public.');
        } else if (err.status === 502) {
          setMoxfieldUrlError('Could not reach Moxfield. Try again or paste the deck list manually.');
        } else {
          setMoxfieldUrlError(err.message);
        }
      } else {
        setMoxfieldUrlError('Failed to import from Moxfield. Is the server running?');
      }
    }
  };

  const handleConfirm = () => {
    importEntries(pendingEntries);
    for (const card of pendingSideboard) {
      addToSideboard(card);
    }
    setMoxfieldDeckId(pendingMoxfieldDeckId);
    if (pendingMoxfieldDeckId) {
      setImportLoading(true);
    }
    onClose();
  };

  const deckCardCount = pendingEntries.reduce((sum, e) => sum + e.quantity, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="relative w-full max-w-xl mx-4 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h2 className="text-base font-semibold text-gray-100">Import Deck List</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {step === 'input' && (
            <>
              {/* Moxfield URL row */}
              <div className="space-y-1">
                <label className="block text-sm text-gray-400">Moxfield URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={moxfieldUrl}
                    onChange={(e) => setMoxfieldUrl(e.target.value)}
                    placeholder="https://www.moxfield.com/decks/…"
                    className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-orange-500"
                  />
                  <button
                    onClick={() => { void handleImportUrl(); }}
                    disabled={!moxfieldUrl.trim()}
                    className="px-3 py-2 text-sm font-medium bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed text-gray-100 rounded-lg transition-colors whitespace-nowrap"
                  >
                    Import URL
                  </button>
                </div>
                {moxfieldUrlError && (
                  <p className="text-sm text-red-400">{moxfieldUrlError}</p>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-2">
                <hr className="flex-1 border-gray-700" />
                <span className="text-xs text-gray-500">or paste a decklist</span>
                <hr className="flex-1 border-gray-700" />
              </div>

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={'// Commander\n1 Atraxa, Praetors\' Voice\n\n// Deck\n1 Sol Ring\n1 Cultivate\n...'}
                rows={14}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-100 placeholder-gray-600 font-mono focus:outline-none focus:border-orange-500 resize-none"
              />
              {error && <p className="text-sm text-red-400">{error}</p>}
            </>
          )}

          {step === 'loading' && (
            <div className="flex flex-col items-center gap-3 py-8">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400">{loadingMessage}</p>
            </div>
          )}

          {step === 'confirm' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-300">
                <span className="text-green-400 font-medium">{deckCardCount}</span> card
                {deckCardCount !== 1 ? 's' : ''} resolved.
                {pendingSideboard.length > 0 && (
                  <span className="text-blue-400 ml-1">
                    {pendingSideboard.length} sideboard.
                  </span>
                )}
                {notFound.length > 0 && (
                  <span className="text-yellow-400 ml-1">
                    {notFound.length} not found.
                  </span>
                )}
              </p>
              {notFound.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <p className="text-xs text-gray-500 mb-1">Not found:</p>
                  {notFound.map((n) => (
                    <p key={n} className="text-xs text-yellow-400">{n}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-700">
          {step === 'confirm' && (
            <button
              onClick={() => setStep('input')}
              className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
            >
              Back
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          {step === 'input' && (
            <button
              onClick={() => { void handleImport(); }}
              disabled={!text.trim()}
              className="px-4 py-2 text-sm font-medium bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              Import
            </button>
          )}
          {step === 'confirm' && (
            <button
              onClick={handleConfirm}
              className="px-4 py-2 text-sm font-medium bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors"
            >
              Add to Deck
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
